import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import ContactForm from '../components/ContactForm'

export default function ContactPage() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const matchedPage = data.find((p) => p.page_slug === 'kontakt')
          if (matchedPage) {
            setPageData(matchedPage)
          }
        }
      })
      .catch((err) => console.error('Failed to fetch contact page data', err))
      .finally(() => setLoading(false))
  }, [])

  const title = pageData?.meta_title || 'Kontakt | Tømrerfirmaet Hansen og Mortensen Fredensborg'
  const description = pageData?.meta_description || 'Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.'

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-7-2-1-1.png" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] w-full bg-neutral-950 flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-7-2-1-1.png"
            alt="Kontakt"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-neutral-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-red-500 text-xs font-extrabold uppercase tracking-widest mb-2 block">
            Hansen &amp; Mortensen
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-white uppercase tracking-tight">
            Kontakt
          </h1>
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wider mt-4">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <span className="text-zinc-600">/</span>
            <span className="text-white">Kontakt</span>
          </div>
        </div>
      </section>

      {/* Main Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left: Contact Details */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div>
                <span className="text-red-700 text-xs font-extrabold uppercase tracking-widest">
                  Information
                </span>
                <h2 className="text-2xl sm:text-4xl font-black text-neutral-950 uppercase tracking-tight mt-2">
                  Kontakt os i dag
                </h2>
                <div className="w-16 h-1 bg-red-700 mt-4" />
              </div>

              <div className="text-zinc-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Har du spørgsmål til vores ydelser, eller ønsker du et uforpligtende tilbud på dit næste projekt? Du er altid velkommen til at kontakte os via formularen eller direkte på telefon eller e-mail.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200">
                  <div className="w-10 h-10 bg-red-700 text-white flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-phone" />
                  </div>
                  <div>
                    <h3 className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Telefon</h3>
                    <div className="flex flex-col gap-1 mt-1">
                      <a href="tel:22115514" className="text-neutral-950 font-extrabold hover:text-red-700 transition-colors whitespace-nowrap">
                        22 11 55 14
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200">
                  <div className="w-10 h-10 bg-red-700 text-white flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-envelope" />
                  </div>
                  <div>
                    <h3 className="text-xs text-zinc-500 uppercase font-bold tracking-wider">E-mail</h3>
                    <a href="mailto:Hogm@hansenogmortensen.dk" className="text-neutral-950 font-extrabold hover:text-red-700 transition-colors block mt-1 text-sm sm:text-base whitespace-normal break-all sm:whitespace-nowrap">
                      Hogm@hansenogmortensen.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200">
                  <div className="w-10 h-10 bg-red-700 text-white flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div>
                    <h3 className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Adresse &amp; CVR</h3>
                    <p className="text-neutral-950 font-extrabold mt-1">
                      Lillevangsvej 206, 3480 Fredensborg
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">CVR nr.: 20945036</p>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="p-6 border border-zinc-200">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-neutral-950 mb-4">
                  Åbningstider
                </h3>
                <div className="space-y-2 text-xs sm:text-sm text-zinc-600">
                  <div className="flex justify-between border-b border-zinc-100 pb-2">
                    <span>Mandag - Torsdag:</span>
                    <span className="text-neutral-950 font-bold">07:00 - 16:00</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-100 pb-2">
                    <span>Fredag:</span>
                    <span className="text-neutral-950 font-bold">07:00 - 14:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Lørdag - Søndag:</span>
                    <span className="text-zinc-400">Lukket</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stylized Map Section */}
      <section className="relative h-96 bg-zinc-100 border-t border-zinc-200 overflow-hidden">
        {/* Stylized Map Placeholder using a beautiful image of Fredensborg or similar */}
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/37f7f16d-7c9c-4f81-b10c-2a08ddb4d637/imgi-20-sorthus-108.png"
          alt="Fredensborg"
          className="w-full h-full object-cover opacity-40 grayscale"
        />
        <div className="absolute inset-0 bg-neutral-950/10" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white p-6 shadow-2xl border border-zinc-200 text-center max-w-xs">
            <i className="fa-solid fa-location-dot text-red-700 text-3xl mb-3" />
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-neutral-950 mb-1">
              Hansen &amp; Mortensen
            </h3>
            <p className="text-zinc-600 text-xs">
              Lillevangsvej 206 <br />
              3480 Fredensborg
            </p>
            <a
              href="https://maps.google.com/?q=Lillevangsvej+206,+3480+Fredensborg"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 text-red-700 hover:text-red-800 text-xs font-bold uppercase tracking-wider"
            >
              Find vej på kort <i className="fa-solid fa-arrow-up-right-from-square text-[10px] ml-1" />
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
